import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAdminDashboardStats, type AdminDashboardStats } from '@/api/adminApi'
import { AdminPage, AdminPanel } from '@/components/Admin'
import styles from './Admin.module.css'

const QUICK_ACTIONS = [
  { to: '/admin/menu', icon: '🏠', label: 'Configurar menu inicial' },
  { to: '/admin/servicos', icon: '✨', label: 'Serviços e categorias' },
  { to: '/admin/profissionais', icon: '👥', label: 'Gerir profissionais' },
  { to: '/admin/disponibilidade', icon: '🕐', label: 'Horários e disponibilidade' },
  { to: '/admin/social', icon: '💬', label: 'Moderar feed social' },
  { to: '/menu', icon: '📱', label: 'Ver app do cliente' },
] as const

type KpiItem = {
  label: string
  value: keyof AdminDashboardStats
  to?: string
}

const KPI_ITEMS: KpiItem[] = [
  { label: 'Agendamentos hoje', value: 'agendamentos_hoje', to: '/admin/agendamentos' },
  { label: 'Pendentes (hoje)', value: 'agendamentos_hoje_pendentes', to: '/admin/agendamentos' },
  { label: 'Agendamentos pendentes', value: 'agendamentos_pendentes', to: '/admin/agendamentos' },
  { label: 'Feedbacks pendentes', value: 'feedbacks_pendentes', to: '/admin/feedbacks' },
  { label: 'Social pendentes', value: 'social_pendentes', to: '/admin/social' },
  { label: 'Utilizadores activos', value: 'usuarios_ativos' },
  { label: 'Profissionais activos', value: 'profissionais_ativos', to: '/admin/profissionais' },
]

export function AdminDashboardHome() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getAdminDashboardStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar indicadores')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <AdminPage
      title="Painel de controle"
      subtitle="Organize o app por blocos: base visual, conteúdo público, operação e comunidade."
      loading={loading}
      error={error}
      wide
    >
      <AdminPanel title="Indicadores" description="Resumo da operação da sua conta.">
        <div className={styles.dashboardKpiGrid}>
          {KPI_ITEMS.map((kpi) => {
            const value = stats ? stats[kpi.value] : 0
            const content = (
              <>
                <span className={styles.dashboardKpiValue}>{value}</span>
                <span className={styles.dashboardKpiLabel}>{kpi.label}</span>
              </>
            )
            return kpi.to ? (
              <Link key={kpi.value} to={kpi.to} className={styles.dashboardKpiCard}>
                {content}
              </Link>
            ) : (
              <div key={kpi.value} className={styles.dashboardKpiCard}>
                {content}
              </div>
            )
          })}
        </div>
      </AdminPanel>

      <AdminPanel
        title="Ações rápidas"
        description="As alterações são guardadas no servidor; o app do cliente lê os dados da API automaticamente."
      >
        <nav className={styles.dashboardQuickActions} aria-label="Ações rápidas">
          {QUICK_ACTIONS.map((action) => (
            <Link key={action.to} to={action.to} className={styles.dashboardQuickAction}>
              <span className={styles.dashboardQuickActionIcon} aria-hidden>
                {action.icon}
              </span>
              {action.label}
            </Link>
          ))}
        </nav>
      </AdminPanel>
    </AdminPage>
  )
}
